import { useMutation, useQuery } from '@tanstack/react-query';
import {
  commonAxiosError,
  commonAxiosSuccess,
  queryClient,
} from '../../../Utils/TanstackQuery/queryClient';
import { httpService } from '../../../Helpers/Fetcher/axios';
import { b2bApi } from './b2bConstant';
import {
  B2BData,
  IB2BInvoiceList,
  IGetAvailableSlotList,
  IGetSpecificAvailableSlot,
  IMyRequestedSlotList,
  ISlotsBookingRequest,
  ISlotsCreate,
  ISlotsOwnSlotList,
} from '../type/b2bBuyerSellerType';
import { IAvailableFilter } from '../B2BProfile/MyMeetingRequest/SlotsCompanyFilter';

export const useB2BProfileUpdate = () => {
  return useMutation({
    mutationFn: (data: FormData) =>
      httpService.patch({ url: `${b2bApi.UPDATE_B2B_PROFILE}`, data }),
    onSuccess: () => {
      commonAxiosSuccess(`Successfully Updated!`);
      queryClient.invalidateQueries({
        queryKey: [b2bApi.GET_B2B_PROFILE],
      });
    },

    onError: commonAxiosError(),
  });
};

export const useGetB2BInvoiceList = () => {
  return useQuery({
    queryKey: [b2bApi.B2B_INVOICE_LIST],
    queryFn: () =>
      httpService.get<IB2BInvoiceList[]>({
        url: b2bApi.B2B_INVOICE_LIST,
      }),
  });
};

export const useGetB2BProfile = () => {
  return useQuery({
    queryKey: [b2bApi.GET_B2B_PROFILE],
    queryFn: () =>
      httpService.get<B2BData>({
        url: b2bApi.GET_B2B_PROFILE,
      }),
  });
};
/* MY REQUESTED MEETING REQUEST */
export const useGetMyMeetingSlotRequestList = ({
  type,
  user_id,
}: {
  type: 'B2B' | 'Exhibitor';
  user_id: number;
}) => {
  return useQuery({
    queryKey: [b2bApi.GET_MY_MEETING_SLOT_REQUEST, user_id],
    queryFn: () =>
      httpService.get<IMyRequestedSlotList[]>({
        url: b2bApi.GET_MY_MEETING_SLOT_REQUEST,
        params: { type, user_id },
      }),
    enabled: true,
  });
};
export const useGetOtherCompanySlotRequestedList = ({
  type,
  user_id,
  slot_id,
}: {
  type: 'B2B' | 'Exhibitor';
  user_id: number;
  slot_id?: string;
}) => {
  return useQuery({
    queryKey: [b2bApi.OTHER_COMPANY_SLOT_REQUEST_LIST, slot_id],
    queryFn: () =>
      httpService.get<IMyRequestedSlotList[]>({
        url: b2bApi.OTHER_COMPANY_SLOT_REQUEST_LIST,
        params: { type, user_id, slot_id },
      }),
  });
};

/* ---------slot Create-------------------- */

export const useUpdateSlotStatus = (slot_id: number) => {
  return useMutation({
    mutationFn: (data: {
      type: 'B2B' | 'Exhibitor';
      user_id: number;
      status: 'Rejected' | 'Accepted' | 'Cancelled';
      rejected_reason?: string;
    }) =>
      httpService.patch({
        url: `${b2bApi.SLOT_REQUEST_STATUS_UPDATE}/${slot_id}`,
        data,
      }),
    onSuccess: () => {
      commonAxiosSuccess(`Successfully Status Updated!`);
      queryClient.invalidateQueries({
        queryKey: [b2bApi.SLOT_REQUEST_STATUS_UPDATE],
      });
    },

    onError: commonAxiosError(),
  });
};
export const useSlotBookingRequest = () => {
  return useMutation({
    mutationFn: (data: ISlotsBookingRequest) =>
      httpService.post({ url: `${b2bApi.BOOKING_SLOT_REQUEST}`, data }),
    onSuccess: () => {
      commonAxiosSuccess(`Successfully Booking!`);

      const queryKeysToInvalidate = [
        b2bApi.GET_ALL_AVAILABLE_SLOTS,
        b2bApi.GET_MY_MEETING_SLOT_REQUEST,
      ];

      queryKeysToInvalidate.forEach((key) => {
        queryClient.invalidateQueries({ queryKey: [key] });
      });
    },

    onError: commonAxiosError(),
  });
};
export const useB2BSlotsCreate = () => {
  return useMutation({
    mutationFn: (data: ISlotsCreate) =>
      httpService.post({ url: `${b2bApi.B2B_SLOTS_CREATE}`, data }),
    onSuccess: () => {
      commonAxiosSuccess(`Successfully Created!`);
      queryClient.invalidateQueries({
        queryKey: [b2bApi.GET_ALL_OWN_SLOTS],
      });
    },

    onError: commonAxiosError(),
  });
};

export const useGetB2BSlotList = ({
  type,
  user_id,
}: {
  type: 'B2B' | 'Exhibitor';
  user_id: number;
}) => {
  return useQuery({
    queryKey: [b2bApi.GET_ALL_OWN_SLOTS],
    queryFn: () =>
      httpService.get<ISlotsOwnSlotList[]>({
        url: b2bApi.GET_ALL_OWN_SLOTS,
        params: { type, user_id },
      }),
  });
};

export const useGetB2BAvailableSlotList = ({
  type,
  user_id,
  business_type,
  filter_type,
  keywords,
}: {
  type: 'B2B' | 'Exhibitor';
  user_id: number;
  filter_type?: any;
  business_type?: string;
  keywords?: string;
}) => {
  return useQuery({
    queryKey: [
      b2bApi.GET_ALL_AVAILABLE_SLOTS,
      filter_type,
      business_type,
      keywords,
    ],
    queryFn: () =>
      httpService.get<IGetAvailableSlotList[]>({
        url: b2bApi.GET_ALL_AVAILABLE_SLOTS + filter_type,
        params: {
          type,
          user_id,
          business_type,
          keywords,
        },
      }),
  });
};

export const useReservedSlot = ({
  type,
  user_id,
}: {
  type: 'B2B' | 'Exhibitor';
  user_id: number;
}) => {
  return useMutation({
    mutationFn: (data: { is_reserved: 0 | 1; slot_id: number }) =>
      httpService.patch({
        url: `${b2bApi.RESERVED_SLOT}/${data?.slot_id}?user_id=${user_id}&type=${type}`,
        data: { is_reserved: data?.is_reserved },
      }),
    onSuccess: () => {
      commonAxiosSuccess(`Successfully Done!`);
      queryClient.invalidateQueries({
        queryKey: [b2bApi.GET_ALL_OWN_SLOTS],
      });
    },

    onError: commonAxiosError(),
  });
};
export const useGetB2BSpecificAvailableSlotList = ({
  type,
  user_id,
  staff_id,
  own_type,
  own_id,
}: {
  type: 'B2B' | 'Exhibitor';
  user_id: number;
  staff_id?: string | null;
  own_type?: string | null;
  own_id?: string | number | null;
}) => {
  return useQuery({
    queryKey: [b2bApi.GET_SPECIFIC_USER_AVAILABLE_SLOTS, user_id],
    queryFn: () =>
      httpService.get<IGetSpecificAvailableSlot>({
        url: b2bApi.GET_SPECIFIC_USER_AVAILABLE_SLOTS,
        params: { type, user_id, staff_id, own_type, own_id },
      }),
  });
};

export const useDeleteB2BSlot = () => {
  return useMutation({
    mutationFn: (data) =>
      httpService.delete({ url: `${b2bApi.UPDATE_B2B_PROFILE}` }),
    onSuccess: () => {
      commonAxiosSuccess(`Successfully Deleted!`);
      queryClient.invalidateQueries({
        queryKey: [b2bApi.GET_B2B_PROFILE],
      });
    },

    onError: commonAxiosError(),
  });
};
